import { useCallback, useMemo } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';
import { useGetCurrentUserQuery } from 'services';
import { getVisibleFilterFields, planFilterNavigation, readFilterValues } from 'components/ArticlesTable/offerFilterSchema';

/**
 * Teklif listesinin uygulanmış filtrelerini URL'den okur ve tüm değişiklikleri URL üzerinden yapar. Filtrelerin tek
 * kaynağı URL'dir; form durumu başka bir yerde (ör. `localStorage`) tutulmaz.
 *
 * `values` yalnız değeri olan alanları içerir; anahtar sayısı uygulanmış alan sayısıdır. Uygulama sayfayı 1'e alır,
 * arama metni, sayfa boyutu, sıralama ve rota kısıtları korunur. Aynı değerlerle uygulama URL'yi değiştirmez.
 */
export const useOfferFilterParams = () => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { data: currentUser } = useGetCurrentUserQuery();

  const fields = useMemo(() => getVisibleFilterFields(currentUser), [currentUser]);
  const values = useMemo(() => readFilterValues(search, fields), [search, fields]);

  const apply = useCallback(
    (nextValues) => {
      const plan = planFilterNavigation(search, nextValues, fields);
      if (plan) {
        navigate({ pathname, search: plan.search }, { replace: plan.replace });
      }
    },
    [fields, navigate, pathname, search]
  );

  /** Alanı tamamen kaldırır; bağımlı alt alanlar da düşer. */
  const removeField = useCallback((key) => apply({ ...values, [key]: undefined }), [apply, values]);

  /** Çoklu alandan tek değeri kaldırır; son değer kaldırılınca alan da düşer. */
  const removeValue = useCallback(
    (key, value) => {
      const current = values[key];
      apply({ ...values, [key]: Array.isArray(current) ? current.filter((item) => item !== value) : undefined });
    },
    [apply, values]
  );

  const clearAll = useCallback(() => apply({}), [apply]);

  return { fields, values, apply, removeField, removeValue, clearAll };
};
